import type { ReactNode } from "react";
import { ExplainerDisclosure } from "./ExplainerDisclosure";

export interface Choice {
  value: string;
  label: string;
  description?: string;
  explainerKey?: string;
}

interface ChoiceGroupProps {
  id: string;
  legend: string;
  choices: Choice[];
  value: string;
  onChange: (value: string) => void;
  hint?: ReactNode;
  error?: string;
  /** At most one — see docs/design/features/W4-01.md "Density budget". */
  explainerKey?: string;
}

export function ChoiceGroup({
  id,
  legend,
  choices,
  value,
  onChange,
  hint,
  error,
  explainerKey,
}: ChoiceGroupProps) {
  const describedBy = [hint ? `${id}-hint` : "", error ? `${id}-error` : ""].filter(Boolean).join(" ") || undefined;
  return (
    <fieldset id={id} className={`choice-group ${error ? "field--error" : ""}`} aria-describedby={describedBy} aria-invalid={Boolean(error)} tabIndex={error ? -1 : undefined}>
      <legend>{legend}</legend>
      {hint && <p id={`${id}-hint`} className="field-hint">{hint}</p>}
      {/* Not referenced by aria-describedby: describing every radio with a
          multi-paragraph definition would make the group unusable with a
          screen reader. Reachable in normal DOM order right after the hint.
          Only rendered — so `useContent()` is only ever called — when a key
          is actually bound, which keeps every existing render of this
          component with no explainerKey working with no ContentProvider. */}
      {explainerKey && <ExplainerDisclosure explainerKey={explainerKey} className="explainer--stem" />}
      {error && <p id={`${id}-error`} className="field-error"><span className="sr-only">Error: </span>{error}</p>}
      <div className="choices">
        {choices.map((choice) => (
          <div className="choice-row" key={choice.value}>
            <label className="choice">
              <input
                type="radio"
                name={id}
                value={choice.value}
                checked={value === choice.value}
                aria-invalid={Boolean(error)}
                aria-describedby={describedBy}
                onChange={(event) => onChange(event.target.value)}
              />
              <span className="choice__control" aria-hidden="true" />
              <span className="choice__copy"><strong>{choice.label}</strong>{choice.description && <small>{choice.description}</small>}</span>
            </label>
            {/* A sibling of the label, never inside it — an interactive
                control inside a <label> would steal the click and toggle
                the radio. See docs/design/features/W4-01.md. Only rendered
                when a key is bound — see the stem-level disclosure above. */}
            {choice.explainerKey && (
              <ExplainerDisclosure explainerKey={choice.explainerKey} className="explainer--option" />
            )}
          </div>
        ))}
      </div>
    </fieldset>
  );
}
